// Whisper transcription in the browser via @huggingface/transformers (WebGPU/WASM).
// Mirrors desktop auto-Whisper for no-caption videos. Slow by design — runs on the
// user's device (no server compute), as the user intends.

let _pipe = null;

export async function getTranscriber(onProgress) {
  if (_pipe) return _pipe;
  const { pipeline } = await import('@huggingface/transformers');
  _pipe = await pipeline('automatic-speech-recognition', 'Xenova/whisper-base', {
    progress_callback: onProgress,
  });
  return _pipe;
}

// Extract 16kHz mono Float32Array from a video/audio Blob (proven in GATE C).
export async function extractAudio16kMono(blob) {
  const ab = await blob.arrayBuffer();
  const AC = window.AudioContext || window.webkitAudioContext;
  const ac = new AC();
  const decoded = await ac.decodeAudioData(ab.slice(0));
  const targetRate = 16000;
  const frames = Math.ceil(decoded.duration * targetRate);
  const offline = new OfflineAudioContext(1, frames, targetRate);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start();
  const rendered = await offline.startRendering();
  const pcm = rendered.getChannelData(0);
  await ac.close();
  return { pcm, durationSec: decoded.duration };
}

// audio: Float32Array @ 16kHz mono.
export async function transcribe(audio, { language = 'id', onProgress } = {}) {
  const pipe = await getTranscriber(onProgress);
  return pipe(audio, { language, return_timestamps: 'word', chunk_length_s: 30 });
}

// High-level: video Blob → normalized transcript for analyze + karaoke.
//   { text, words:[{word,start,end}], segments:[{start,end,text}] }
export async function transcribeVideo(blob, { language = 'id', onProgress } = {}) {
  const { pcm } = await extractAudio16kMono(blob);
  const out = await transcribe(pcm, { language, onProgress });

  const words = (out.chunks || [])
    .filter((c) => Array.isArray(c.timestamp))
    .map((c) => ({
      word: (c.text || '').trim(),
      start: c.timestamp[0] ?? 0,
      end: c.timestamp[1] ?? (c.timestamp[0] ?? 0) + 0.4,
    }))
    .filter((w) => w.word);

  // Group words into ~10s segments for the analyzer's timestamped transcript.
  const segments = [];
  let cur = null;
  for (const w of words) {
    if (!cur || w.start - cur.start > 10) {
      cur = { start: w.start, end: w.end, text: w.word };
      segments.push(cur);
    } else {
      cur.text += ' ' + w.word;
      cur.end = w.end;
    }
  }
  return { text: out.text || words.map((w) => w.word).join(' '), words, segments };
}
